import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  NotificationAggregateItem,
  NotificationListResponse,
  NotificationMarkViewedBody,
} from '@magimp/shared';
import { markNotificationViewed } from '../../../api/notifications.js';
import { NOTIFICATIONS_LIST_QK } from './useNotificationsList.js';

// WHY (#1359, #1380): both fields derive from the by-target schema so the
//   hook stays aligned with its accepted pairs, including LEAD /
//   lead_callback as exercised by useAutoMarkLeadCallbackViewed.
export interface MarkViewedByTargetVars {
  entityType: Extract<NotificationMarkViewedBody, { by: 'target' }>['entityType'];
  entityId: string;
  notificationType: Extract<NotificationMarkViewedBody, { by: 'target' }>['notificationType'];
}

/**
 * @aiPurpose mark every aggregate for a (entityType, entityId,
 *   notificationType) target viewed in one server round-trip. Used by
 *   drawer-tab-open / drawer-mount auto-view paths in #1072.
 * @aiInvariant the server returns a single canonical aggregate row;
 *   optimistic update zeroes ALL matching rows because the backend
 *   collapses them server-side and we must not leave a stale dot on the
 *   row badge while the request is in flight.
 */
export function useMarkNotificationViewedByTarget() {
  const queryClient = useQueryClient();
  return useMutation<NotificationAggregateItem, Error, MarkViewedByTargetVars>({
    mutationFn: (vars) =>
      markNotificationViewed({
        by: 'target',
        entityType: vars.entityType,
        entityId: vars.entityId,
        notificationType: vars.notificationType,
      }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_LIST_QK });
      const prev = queryClient.getQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK);
      if (prev) {
        const nowIso = new Date().toISOString();
        const next = prev.map((row) =>
          row.entityType === vars.entityType &&
          row.entityId === vars.entityId &&
          row.notificationType === vars.notificationType
            ? { ...row, unreadCount: 0, firstUnreadAt: null, viewedAt: nowIso }
            : row,
        );
        queryClient.setQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK, next);
      }
      return { prev };
    },
    onError: (_err, _vars, context) => {
      const prev = (context as { prev?: NotificationListResponse } | undefined)?.prev;
      if (prev) {
        queryClient.setQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK, prev);
      } else {
        void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_QK });
      }
    },
    onSuccess: (next) => {
      // WHY: the by-target mutation returns the now-viewed canonical row;
      //   the optimistic pass already zeroed every matching row, so we
      //   only need to swap the canonical id back in for the server's
      //   authoritative `viewedAt`/`lastEventAt` clock.
      const prev = queryClient.getQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK);
      if (prev) {
        const reconciled = prev.map((row) => (row.id === next.id ? next : row));
        queryClient.setQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK, reconciled);
      }
    },
  });
}
